
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { 
  FileText, 
  Layers, 
  Tag, 
  DollarSign, 
  Users, 
  Grid3X3, 
  Plus, 
  FileSearch,
  Settings,
  Building,
  Building2,
  Coins,
  UserPlus,
  FolderPlus,
  Zap,
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  Clock,
  Calendar,
  User,
  Mail,
  BookOpen,
  Sliders,
  Tags,
  GitBranch,
  HeartPulse,
  FileSignature,
  ClipboardCheck,
  Upload
} from "lucide-react";
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle
} from "@/components/ui/navigation-menu";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/components/ThemeProvider";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useAdvisorAccess } from "@/hooks/useAdvisorAccess";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useTranslation } from "react-i18next";
import { Briefcase } from "lucide-react";

interface NavigationListItemProps extends React.ComponentPropsWithoutRef<"a"> {
  title: string;
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  NavigationListItemProps
>(({ className, title, children, ...props }, ref) => {
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";
  
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "group block select-none space-y-1 rounded-xl p-4 leading-none no-underline outline-none",
            "transition-all duration-200 transform hover:scale-[1.02]",
            "border border-transparent hover:border-neutral-200 dark:hover:border-neutral-700",
            "hover:bg-gradient-to-r hover:from-primary-50 hover:to-neutral-50 dark:hover:from-primary-900/20 dark:hover:to-neutral-800",
            "hover:shadow-soft focus:shadow-soft-lg",
            is24Seven
              ? "hover:bg-gray-100 hover:text-24seven-text focus:bg-gray-100 focus:text-24seven-text"
              : "hover:text-primary-700 dark:hover:text-primary-300",
            className
          )}
          {...props}
        >
          <div className={cn(
            "text-base font-semibold leading-none mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors",
            is24Seven && "text-24seven-text"
          )}>
            {title}
          </div>
          <div className={cn(
            "line-clamp-2 text-sm leading-snug",
            is24Seven ? "text-24seven-muted" : "text-muted-foreground"
          )}>
            {children}
          </div>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export function DesktopNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { theme } = useTheme();
  const is24Seven = theme === "24seven";
  const { 
    canUseDocumentExtraction, 
    canUseSalesAnalytics,
    canUseCostOptimization,
    canUseDataHealth,
    canUseDashboard,
    canUseCustomFields,
    canUseGroupedContracts,
    canUseCalendarView,
    canUseGoverningDocuments,
  } = useFeatureAccess();
  const { useHasRoleQuery } = useUserRoles();
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  const { useMyAdvisorAccessQuery } = useAdvisorAccess();
  const { data: advisorAccessList } = useMyAdvisorAccessQuery();
  const { workspaces } = useWorkspace();
  const { t } = useTranslation();

  const activeAdvisorAccessCount = React.useMemo(
    () =>
      (advisorAccessList || []).filter(
        (a) =>
          a.accepted_at && !a.revoked_at && new Date(a.expires_at) > new Date()
      ).length,
    [advisorAccessList]
  );
  const hasBureauAccess = activeAdvisorAccessCount > 0;
  const isMultiWorkspaceMember = (workspaces?.length || 0) >= 2;
  const canSeeCockpitInTools = isMultiWorkspaceMember && !hasBureauAccess;

  // Check if any premium tools are available
  const hasAnyPremiumTools = canUseSalesAnalytics || canUseCostOptimization || canUseDataHealth || canUseGoverningDocuments || canSeeCockpitInTools;

  
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const handleInternalNavigation = (event: React.MouseEvent<HTMLElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target as HTMLElement;
    const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
    const href = anchor?.getAttribute("href");

    if (!href || !href.startsWith("/")) return;

    event.preventDefault();
    navigate(href);
  };
  
  return (
    <NavigationMenu onClickCapture={handleInternalNavigation}>
      <NavigationMenuList className="flex items-center space-x-1">
        {/* Dashboard Link - only show if user has dashboard access */}
        {canUseDashboard && (
          <NavigationMenuItem>
            <Link to="/dashboard">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/dashboard")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <BarChart3 className="h-4 w-4" />
                <span>Dashboard</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Contracts Menu */}
        <NavigationMenuItem>
          <NavigationMenuTrigger 
            className={cn(
              "group px-5 py-2 rounded-full font-medium transition-all duration-200",
              "flex items-center gap-2",
              is24Seven
                ? isActive("/contracts") 
                  ? "bg-secondary text-secondary-foreground shadow-md" 
                  : "hover:bg-muted text-foreground"
                : isActive("/contracts") 
                  ? "bg-secondary text-secondary-foreground shadow-md" 
                  : "hover:bg-muted text-foreground"
            )}
          >
            <FileText className="h-4 w-4" />
            Contracts
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="p-6 w-[600px] md:w-[700px] lg:w-[800px]">
              {/* Featured Section */}
              <div className="mb-6">
                <NavigationMenuLink asChild>
                  <a
                    className="flex h-24 w-full select-none flex-row items-center justify-between rounded-3xl bg-secondary text-secondary-foreground p-6 no-underline outline-none hover:shadow-lg transition-all duration-200"
                    href="/contracts"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-2 bg-primary rounded-2xl">
                        <FileText className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold">All Contracts</div>
                        <p className="text-sm opacity-80">Browse and manage your contract portfolio</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">→</div>
                    </div>
                  </a>
                </NavigationMenuLink>
              </div>

              {/* Quick Actions Grid */}
              <div className="grid grid-cols-3 gap-4">
                <NavigationMenuLink asChild>
                  <a
                    href={`/contracts/quick-add?returnTo=${encodeURIComponent(currentPath)}`}
                    className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-success/10 rounded-2xl group-hover:bg-success/20 transition-colors">
                        <Zap className="h-4 w-4 text-success" />
                      </div>
                      <Badge className="bg-success/10 text-success border-0">
                        <Clock className="mr-1 h-3 w-3" />
                        Fast
                      </Badge>
                    </div>
                    <div className="font-semibold text-sm mb-1">Quick Add</div>
                    <p className="text-xs text-muted-foreground">Create contract quickly with essential details</p>
                  </a>
                </NavigationMenuLink>

                <NavigationMenuLink asChild>
                  <a
                    href="/contracts/new"
                    className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-primary/10 rounded-2xl group-hover:bg-primary/20 transition-colors">
                        <Plus className="h-4 w-4 text-primary-foreground" />
                      </div>
                      <Badge className="bg-primary/10 text-primary-foreground border-0">
                        <FileText className="mr-1 h-3 w-3" />
                        Full
                      </Badge>
                    </div>
                    <div className="font-semibold text-sm mb-1">New Contract</div>
                    <p className="text-xs text-muted-foreground">Create comprehensive contract with all details</p>
                  </a>
                </NavigationMenuLink>
                {canUseDocumentExtraction && (
                  <NavigationMenuLink asChild>
                    {isAdmin ? (
                      <a
                        href="/contracts/extract"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-warning/10 rounded-2xl group-hover:bg-warning/20 transition-colors">
                            <FileSearch className="h-4 w-4 text-warning" />
                          </div>
                          <Badge className="bg-warning/10 text-warning border-0">
                            <TrendingUp className="mr-1 h-3 w-3" />
                            AI
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm mb-1">Extract from Document</div>
                        <p className="text-xs text-muted-foreground">AI-powered contract extraction from PDFs</p>
                      </a>
                    ) : (
                      <div className="block p-4 rounded-3xl border border-border bg-card opacity-50 cursor-not-allowed">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-warning/10 rounded-2xl">
                            <FileSearch className="h-4 w-4 text-warning" />
                          </div>
                          <Badge className="bg-warning/10 text-warning border-0">
                            <TrendingUp className="mr-1 h-3 w-3" />
                            AI
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm mb-1 flex items-center gap-2">
                          Extract from Document
                          <span className="text-xs font-normal text-muted-foreground">(Coming soon)</span>
                        </div>
                        <p className="text-xs text-muted-foreground">AI-powered contract extraction from PDFs</p>
                      </div>
                    )}
                  </NavigationMenuLink>
                )}

                <NavigationMenuLink asChild>
                  <Link
                    to="/contracts/import"
                    className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="p-2 bg-accent/50 rounded-2xl group-hover:bg-accent transition-colors">
                        <Upload className="h-4 w-4 text-foreground" />
                      </div>
                      <Badge className="bg-accent/50 text-foreground border-0">
                        <FileText className="mr-1 h-3 w-3" />
                        Excel
                      </Badge>
                    </div>
                    <div className="font-semibold text-sm mb-1">Import</div>
                    <p className="text-xs text-muted-foreground">Import contracts from Excel file</p>
                  </Link>
                </NavigationMenuLink>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
        
        {/* Settings & Management Menu */}
        <NavigationMenuItem>
          <NavigationMenuTrigger 
            className={cn(
              "group px-5 py-2 rounded-full font-medium transition-all duration-200",
              "flex items-center gap-2",
              is24Seven
                ? (isActive("/categories") || 
                   isActive("/contract-types") || 
                   isActive("/currencies") || 
                   isActive("/contacts") || 
                   isActive("/suppliers") || 
                   isActive("/groups"))
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
                : (isActive("/categories") || 
                   isActive("/contract-types") || 
                   isActive("/currencies") || 
                   isActive("/contacts") || 
                   isActive("/suppliers") || 
                   isActive("/groups"))
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
            )}
          >
            <Settings className="h-4 w-4" />
            Setup & Manage
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="p-6 w-[700px] md:w-[800px] lg:w-[900px]">
              {/* Management Categories */}
              <div className="grid grid-cols-1 gap-6">
                {/* Business Setup Section */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center">
                    <Building className="mr-2 h-4 w-4" />
                    Business Setup
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <NavigationMenuLink asChild>
                      <a
                        href="/categories"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-warning/10 rounded-2xl group-hover:bg-warning/20 transition-colors">
                            <Tag className="h-4 w-4 text-warning" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Categories</div>
                        <p className="text-xs text-muted-foreground">Organize contracts by category</p>
                      </a>
                    </NavigationMenuLink>

                    <NavigationMenuLink asChild>
                      <a
                        href="/contract-types"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-purple-500/10 rounded-2xl group-hover:bg-purple-500/20 transition-colors">
                            <Layers className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Contract Types</div>
                        <p className="text-xs text-muted-foreground">Define different contract types</p>
                      </a>
                    </NavigationMenuLink>

                    <NavigationMenuLink asChild>
                      <a
                        href="/currencies"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-success/10 rounded-2xl group-hover:bg-success/20 transition-colors">
                            <Coins className="h-4 w-4 text-success" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Currencies</div>
                        <p className="text-xs text-muted-foreground">Manage currencies and exchange rates</p>
                      </a>
                    </NavigationMenuLink>

                    {canUseDocumentExtraction && (
                      <NavigationMenuLink asChild>
                        <a
                          href="/contract-templates"
                          className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-cyan-500/10 rounded-2xl group-hover:bg-cyan-500/20 transition-colors">
                              <FileSignature className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                            </div>
                          </div>
                          <div className="font-semibold text-sm mb-1">Document Templates</div>
                          <p className="text-xs text-muted-foreground">Create contract document templates</p>
                        </a>
                      </NavigationMenuLink>
                    )}

                    {canUseDocumentExtraction && (
                      <NavigationMenuLink asChild>
                        <a
                          href="/clause-library"
                          className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-amber-500/10 rounded-2xl group-hover:bg-amber-500/20 transition-colors">
                              <BookOpen className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            </div>
                          </div>
                          <div className="font-semibold text-sm mb-1">Clause Library</div>
                          <p className="text-xs text-muted-foreground">Reusable clause snippets</p>
                        </a>
                      </NavigationMenuLink>
                    )}
                  </div>
                </div>

                {/* Relationships Section */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center">
                    <Users className="mr-2 h-4 w-4" />
                    Relationships & Organization
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <NavigationMenuLink asChild>
                      <a
                        href="/suppliers"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-emerald-500/10 rounded-2xl group-hover:bg-emerald-500/20 transition-colors">
                            <Building2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Vendors</div>
                        <p className="text-xs text-muted-foreground">Manage your vendors and their contacts</p>
                      </a>
                    </NavigationMenuLink>

                    <NavigationMenuLink asChild>
                      <a
                        href="/contacts"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-blue-500/10 rounded-2xl group-hover:bg-blue-500/20 transition-colors">
                            <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Contacts</div>
                        <p className="text-xs text-muted-foreground">Manage vendors and business contacts</p>
                      </a>
                    </NavigationMenuLink>

                    {canUseGroupedContracts && (
                      <NavigationMenuLink asChild>
                        <a
                          href="/groups"
                          className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-indigo-500/10 rounded-2xl group-hover:bg-indigo-500/20 transition-colors">
                              <Grid3X3 className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                            </div>
                          </div>
                          <div className="font-semibold text-sm mb-1">Departments</div>
                          <p className="text-xs text-muted-foreground">Organize contracts by department</p>
                        </a>
                      </NavigationMenuLink>
                    )}

                    {canUseCustomFields && (
                      <NavigationMenuLink asChild>
                        <a
                          href="/settings/custom-fields"
                          className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="p-2 bg-teal-500/10 rounded-2xl group-hover:bg-teal-500/20 transition-colors">
                              <Sliders className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                            </div>
                          </div>
                          <div className="font-semibold text-sm mb-1">Custom Fields</div>
                          <p className="text-xs text-muted-foreground">Define custom contract fields</p>
                        </a>
                      </NavigationMenuLink>
                    )}

                    <NavigationMenuLink asChild>
                      <a
                        href="/settings/tags"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-pink-500/10 rounded-2xl group-hover:bg-pink-500/20 transition-colors">
                            <Tags className="h-4 w-4 text-pink-600 dark:text-pink-400" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Tags</div>
                        <p className="text-xs text-muted-foreground">Manage reusable contract tags</p>
                      </a>
                    </NavigationMenuLink>
                  </div>
                </div>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Tools Menu - Only show if user has access to any premium tools */}
        {hasAnyPremiumTools && (
          <NavigationMenuItem>
            <NavigationMenuTrigger 
              className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                (isActive("/approval-workflows") || isActive("/cost-optimization") || isActive("/sales-analytics") || isActive("/data-health") || isActive("/portfolio-assessment") || isActive("/governing-documents") || (canSeeCockpitInTools && isActive("/advisor/cockpit")))
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}
            >
              <Zap className="h-4 w-4" />
              Tools
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="p-6 w-[700px]">
                <div className="grid grid-cols-3 gap-4">
                  {canUseSalesAnalytics && (
                    <NavigationMenuLink asChild>
                      <a
                        href="/sales-analytics"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-blue-500/10 rounded-2xl group-hover:bg-blue-500/20 transition-colors">
                            <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <Badge className="bg-blue-500/10 text-blue-600 border-0">
                            New
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm mb-1">Sales Analytics</div>
                        <p className="text-xs text-muted-foreground">Analyze sales by product, customer & period</p>
                      </a>
                    </NavigationMenuLink>
                  )}

                  {canUseSalesAnalytics && (
                    <NavigationMenuLink asChild>
                      <a
                        href="/approval-workflows"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-purple-500/10 rounded-2xl group-hover:bg-purple-500/20 transition-colors">
                            <GitBranch className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Approval Workflows</div>
                        <p className="text-xs text-muted-foreground">Configure multi-stakeholder approval chains</p>
                      </a>
                    </NavigationMenuLink>
                  )}

                  {canUseCostOptimization && (
                    <NavigationMenuLink asChild>
                      <a
                        href="/cost-optimization"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-emerald-500/10 rounded-2xl group-hover:bg-emerald-500/20 transition-colors">
                            <TrendingDown className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          </div>
                        </div>
                        <div className="font-semibold text-sm mb-1">Cost Optimization</div>
                        <p className="text-xs text-muted-foreground">AI-powered cost reduction insights</p>
                      </a>
                    </NavigationMenuLink>
                  )}

                  {canUseDataHealth && (
                    <NavigationMenuLink asChild>
                      <a
                        href="/data-health"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-rose-500/10 rounded-2xl group-hover:bg-rose-500/20 transition-colors">
                            <HeartPulse className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                          </div>
                          <Badge className="bg-rose-500/10 text-rose-600 border-0">
                            New
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm mb-1">Data Health</div>
                        <p className="text-xs text-muted-foreground">Monitor and improve contract data quality</p>
                      </a>
                    </NavigationMenuLink>
                  )}

                  {canUseDataHealth && (
                    <NavigationMenuLink asChild>
                      <a
                        href="/portfolio-assessment"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-indigo-500/10 rounded-2xl group-hover:bg-indigo-500/20 transition-colors">
                            <ClipboardCheck className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <Badge className="bg-indigo-500/10 text-indigo-600 border-0">
                            New
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm mb-1">Portfolio Assessment</div>
                        <p className="text-xs text-muted-foreground">Contract maturity score & report</p>
                      </a>
                    </NavigationMenuLink>
                  )}

                  {canUseGoverningDocuments && (
                    <NavigationMenuLink asChild>
                      <a
                        href="/governing-documents"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-sky-500/10 rounded-2xl group-hover:bg-sky-500/20 transition-colors">
                            <FileText className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                          </div>
                          <Badge className="bg-sky-500/10 text-sky-600 border-0">
                            New
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm mb-1">Governing Documents</div>
                        <p className="text-xs text-muted-foreground">Manage procedures, instructions & guidelines</p>
                      </a>
                    </NavigationMenuLink>
                  )}

                  {canSeeCockpitInTools && (
                    <NavigationMenuLink asChild>
                      <a
                        href="/advisor/cockpit"
                        className="group block p-4 rounded-3xl border border-border hover:shadow-md transition-all duration-200 bg-card"
                      >
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="p-2 bg-amber-500/10 rounded-2xl group-hover:bg-amber-500/20 transition-colors">
                            <Briefcase className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                          </div>
                          <Badge className="bg-amber-500/10 text-amber-600 border-0">
                            {t("bureauCockpit.badge.multiEntity", "Multi-entity")}
                          </Badge>
                        </div>
                        <div className="font-semibold text-sm mb-1">
                          {t("bureauCockpit.nav", "Cockpit")}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            "bureauCockpit.toolsDesc",
                            "Cross-workspace contract & spend overview"
                          )}
                        </p>
                      </a>
                    </NavigationMenuLink>
                  )}
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        )}

        {/* Bureau Cockpit - silently hidden unless user has active advisor access */}
        {hasBureauAccess && (
          <NavigationMenuItem>
            <Link to="/advisor/cockpit">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/advisor/cockpit")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <Briefcase className="h-4 w-4" />
                <span>{t("bureauCockpit.nav", "Cockpit")}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Documentation Link */}
        <NavigationMenuItem>
          <Link to="/documentation">
            <div className={cn(
              "group px-5 py-2 rounded-full font-medium transition-all duration-200",
              "flex items-center gap-2",
              isActive("/documentation")
                ? "bg-secondary text-secondary-foreground shadow-md"
                : "hover:bg-muted text-foreground"
            )}>
              <BookOpen className="h-4 w-4" />
              <span>Help</span>
            </div>
          </Link>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
